/**
 * 背景地図（ベースマップ）の出し分け。
 *
 * self_hosted_map フラグが有効な会社は、自社で用意した地図を使う。
 *   標準 : OpenStreetMap（ODbL）から作った PMTiles を自社VPSから配信（api.unison-sea.com/tiles）
 *   衛星 : Sentinel-2 cloudless（EOX・出典表示すれば商用利用可）
 *
 * フラグが無効な会社は従来どおり OpenStreetMap 公式タイルと Esri を読む。従来側は
 *   ・OSM公式タイル … 利用ポリシー上「商用サービスはいつアクセスを止められてもおかしくない」
 *   ・Esri World Imagery … ArcGISのライセンス契約が前提で、無契約の直接読み込みは規約外
 * という状態で外販には使えないため、ユニゾンで表示を検証したら全社ONにしてこちら側へ寄せる。
 *
 * PMTiles は「タイルを1ファイルにまとめたもの」で、ブラウザが HTTP Range で必要な部分だけ
 * 読む。タイルサーバーのプロセスは不要で、nginx が静的ファイルとして返すだけで動く。
 */
import L from 'leaflet';
import { leafletLayer } from 'protomaps-leaflet';
import { hasFeature } from './featureFlags';
import { getApiBase } from '../services/api';

export type BasemapType = 'standard' | 'satellite';

/** この会社は自社配信の地図を使うか */
export function isSelfHostedBasemap(): boolean {
  return hasFeature('self_hosted_map');
}

/**
 * 自社タイルの配信元。
 * 既定では API と同じホストの /tiles（api.unison-sea.com/api → api.unison-sea.com/tiles）。
 * ローカル開発などで別の場所を見たいときは VITE_TILES_BASE_URL で上書きする。
 */
export function getTilesBase(): string {
  const override = import.meta.env.VITE_TILES_BASE_URL as string | undefined;
  if (override) return override.replace(/\/+$/, '');
  return getApiBase().replace(/\/+$/, '').replace(/\/api$/, '') + '/tiles';
}

// ── 出典表記 ────────────────────────────────────────────────────────────────
// OSMデータはODbL。PMTiles は OpenStreetMap の Produced Work なので、地図を表示している
// 間ずっと見えるところに © OpenStreetMap を出す必要がある。
const ATTR_OSM_SELF =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors';
// Sentinel-2 cloudless は EOX が指定する文言をそのまま使う（リンク付き）。
const ATTR_S2 =
  '<a href="https://s2maps.eu" target="_blank" rel="noopener noreferrer">Sentinel-2 cloudless</a> by ' +
  '<a href="https://eox.at" target="_blank" rel="noopener noreferrer">EOX IT Services GmbH</a>' +
  '（Contains modified Copernicus Sentinel data 2025）';
// 従来（フラグOFF）の会社が読んでいる外部サービス
const ATTR_OSM_PUBLIC =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors';
const ATTR_ESRI = '&copy; <a href="https://www.esri.com/" target="_blank" rel="noopener noreferrer">Esri</a>';

/** 自社PMTilesのURL。ファイル名は更新時も変えない（更新スクリプトが同名で置き換える） */
export function pmtilesUrl(): string {
  return `${getTilesBase()}/japan.pmtiles`;
}

/**
 * 背景地図レイヤーを作る。mapType と会社フラグの組み合わせで中身が変わる。
 * 呼び出し側は返ってきたレイヤーを addTo(map) して、不要になったら removeLayer するだけでよい。
 */
export function createBaseLayer(mapType: BasemapType = 'standard'): L.Layer {
  const selfHosted = isSelfHostedBasemap();

  if (mapType === 'satellite') {
    if (selfHosted) {
      // Sentinel-2 cloudless（解像度10m）。z15までが実データで、それ以上は拡大表示になる。
      return L.tileLayer(
        'https://tiles.maps.eox.at/wmts/1.0.0/s2cloudless-2025_3857/default/g/{z}/{y}/{x}.jpg',
        { attribution: ATTR_S2, maxZoom: 19, maxNativeZoom: 15 }
      );
    }
    return L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      { attribution: ATTR_ESRI, maxZoom: 19 }
    );
  }

  if (selfHosted) {
    // PMTiles（ベクトルタイル）を canvas に描画する。lang:'ja' で日本語の地名を優先する。
    // z15までのデータなので、それ以上は maxDataZoom で拡大して描く（文字は潰れない）。
    return leafletLayer({
      url: pmtilesUrl(),
      flavor: 'light',
      lang: 'ja',
      maxDataZoom: 15,
      maxZoom: 19,
      attribution: ATTR_OSM_SELF,
    }) as unknown as L.Layer;
  }

  return L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: ATTR_OSM_PUBLIC,
    maxZoom: 19,
  });
}

/**
 * 海図記号（灯台・航行標識）のオーバーレイ。
 * OpenSeaMap のタイルはボランティア運営のサーバーなので、自社配信に切り替えた会社では
 * 読みに行かない。灯台・航路標識は海しる（MSIL）のレイヤーで代替できる。
 */
export function createSeamarkLayer(): L.Layer | null {
  if (isSelfHostedBasemap()) return null;
  return L.tileLayer('https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png', {
    attribution:
      '&copy; <a href="http://www.openseamap.org" target="_blank" rel="noopener noreferrer">OpenSeaMap</a>',
    maxZoom: 19,
    zIndex: 2,
  });
}
